/**
 * AdvancedAnalyticsSection
 * ------------------------
 * Three admin-only analytics cards that live at the bottom of the Admin
 * Analytics Dashboard:
 *
 *   1. Status Transition TAT — average / min / max hours between every
 *      status change of every ticket in the selected window.
 *   2. Post-SLA-Breach TAT — same stats but only for tickets that passed
 *      the breach milestone; the first row highlights breach -> resolved.
 *   3. Resolver & Creator Activity Leaderboard — top resolvers by
 *      satisfied-feedback %, top creators by on-time feedback submission
 *      %, and a flag list for anyone with >= 3 unanswered feedbacks.
 *
 * The parent grants an optional date range (from / to). All three datasets
 * fetch in parallel. The product spec tolerates a 30-40 s delay so we keep
 * a generous client timeout (45s, configured in api.js) and surface a
 * single loading indicator.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertTriangle, Trophy, Flag, RefreshCw, Loader2 } from 'lucide-react';
import { analyticsAPI } from '@/lib/api';

const fmt = (n) => (typeof n === 'number' ? n.toFixed(2) : '—');
const pct = (n) => (typeof n === 'number' ? `${n.toFixed(1)}%` : '—');

const AdvancedAnalyticsSection = () => {
  // Default window: last 30 days.
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(weekAgo);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [transitionData, setTransitionData] = useState(null);
  const [breachData, setBreachData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      const [trans, breach, leaderboard] = await Promise.all([
        analyticsAPI.getStatusTransitionTAT(params),
        analyticsAPI.getSlaBreachTAT(params),
        analyticsAPI.getActivityLeaderboard(params),
      ]);
      setTransitionData(trans.data);
      setBreachData(breach.data);
      setLeaderboardData(leaderboard.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load advanced analytics');
      if (process.env.NODE_ENV !== 'production') {
        console.debug('advanced analytics load failed:', err?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="space-y-4" data-testid="advanced-analytics-section">
      {/* Date range + refresh */}
      <Card className="bg-slate-900/60 border-slate-700">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="space-y-1 flex-1">
            <Label className="text-slate-300 text-xs">From date</Label>
            <Input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="adv-from-date"
            />
          </div>
          <div className="space-y-1 flex-1">
            <Label className="text-slate-300 text-xs">To date</Label>
            <Input
              type="date"
              value={toDate}
              min={fromDate}
              max={today}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="adv-to-date"
            />
          </div>
          <Button
            onClick={fetchAll}
            disabled={loading}
            className="bg-sky-600 hover:bg-sky-700 min-w-[130px]"
            data-testid="adv-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card className="bg-red-950/40 border-red-800">
          <CardContent className="p-4 text-red-200 text-sm" data-testid="adv-analytics-error">
            {error}
          </CardContent>
        </Card>
      )}

      {/* 1) Status Transition TAT */}
      <Card className="bg-slate-900/60 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Clock className="w-5 h-5 text-sky-400" />
            Status Transition TAT
          </CardTitle>
          <CardDescription className="text-slate-400 text-xs">
            Average time spent between each ticket status update. Sample size: {transitionData?.samples ?? 0} transitions across {transitionData?.total_tickets ?? 0} tickets.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Computing transitions (can take up to 40s)…
            </div>
          ) : transitionData?.transitions?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="adv-transition-table">
                <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="py-2 px-3">From</th>
                    <th className="py-2 px-3">To</th>
                    <th className="py-2 px-3 text-right">Avg (h)</th>
                    <th className="py-2 px-3 text-right">Min (h)</th>
                    <th className="py-2 px-3 text-right">Max (h)</th>
                    <th className="py-2 px-3 text-right">Samples</th>
                  </tr>
                </thead>
                <tbody className="text-slate-200">
                  {transitionData.transitions.slice(0, 15).map((t, i) => (
                    <tr key={`${t.from}-${t.to}-${i}`} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-2 px-3">{t.from}</td>
                      <td className="py-2 px-3">{t.to}</td>
                      <td className="py-2 px-3 text-right font-mono">{fmt(t.avg_hours)}</td>
                      <td className="py-2 px-3 text-right font-mono text-emerald-300">{fmt(t.min_hours)}</td>
                      <td className="py-2 px-3 text-right font-mono text-amber-300">{fmt(t.max_hours)}</td>
                      <td className="py-2 px-3 text-right font-mono">{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm py-6">No status transitions recorded in this window.</p>
          )}
        </CardContent>
      </Card>

      {/* 2) Post-Breach TAT */}
      <Card className="bg-slate-900/60 border-red-900/50">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Post-SLA-Breach TAT
          </CardTitle>
          <CardDescription className="text-slate-400 text-xs">
            TAT between status updates for tickets that reached SLA BREACHED.
            {breachData && (
              <> &nbsp;·&nbsp; Breached tickets: <strong className="text-red-300">{breachData.total_breached_tickets ?? breachData.breached_tickets ?? 0}</strong>
                {breachData.time_to_resolution_after_breach?.count > 0 && (
                  <> &nbsp;·&nbsp; Avg breach → resolved: <strong className="text-amber-300">{fmt(breachData.time_to_resolution_after_breach.avg_hours)} h</strong></>
                )}
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Crunching post-breach data…
            </div>
          ) : breachData?.transitions_after_breach?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="adv-breach-table">
                <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="py-2 px-3">From</th>
                    <th className="py-2 px-3">To</th>
                    <th className="py-2 px-3 text-right">Avg (h)</th>
                    <th className="py-2 px-3 text-right">Min (h)</th>
                    <th className="py-2 px-3 text-right">Max (h)</th>
                    <th className="py-2 px-3 text-right">Samples</th>
                  </tr>
                </thead>
                <tbody className="text-slate-200">
                  {breachData.transitions_after_breach.slice(0, 12).map((t, i) => (
                    <tr key={`${t.from}-${t.to}-${i}`} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-2 px-3">{t.from}</td>
                      <td className="py-2 px-3">{t.to}</td>
                      <td className="py-2 px-3 text-right font-mono">{fmt(t.avg_hours)}</td>
                      <td className="py-2 px-3 text-right font-mono text-emerald-300">{fmt(t.min_hours)}</td>
                      <td className="py-2 px-3 text-right font-mono text-red-300">{fmt(t.max_hours)}</td>
                      <td className="py-2 px-3 text-right font-mono">{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm py-6">No post-breach transitions in this window.</p>
          )}
        </CardContent>
      </Card>

      {/* 3) Activity Leaderboard */}
      <Card className="bg-slate-900/60 border-emerald-900/50">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Trophy className="w-5 h-5 text-amber-400" />
            Resolver & Creator Activity Leaderboard
          </CardTitle>
          <CardDescription className="text-slate-400 text-xs">
            Top resolvers by satisfied-feedback %, top creators by on-time feedback submission %, and auto-flag of users with &ge; 3 missed feedbacks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Tallying leaderboard…
            </div>
          ) : (
            <>
              {/* Top Resolvers */}
              <div>
                <h4 className="text-emerald-300 text-sm font-semibold mb-2">Top Resolvers</h4>
                {leaderboardData?.top_resolvers?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left" data-testid="adv-top-resolvers-table">
                      <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
                        <tr>
                          <th className="py-2 px-3">Name</th>
                          <th className="py-2 px-3">Email</th>
                          <th className="py-2 px-3 text-right">Resolved</th>
                          <th className="py-2 px-3 text-right">Satisfied</th>
                          <th className="py-2 px-3 text-right">Not Sat.</th>
                          <th className="py-2 px-3 text-right">Sat %</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-200">
                        {leaderboardData.top_resolvers.map((r, i) => (
                          <tr key={r.email || i} className="border-b border-slate-800 hover:bg-slate-800/50">
                            <td className="py-2 px-3">{r.name}</td>
                            <td className="py-2 px-3 text-slate-400 text-xs">{r.email}</td>
                            <td className="py-2 px-3 text-right font-mono">{r.resolved}</td>
                            <td className="py-2 px-3 text-right font-mono text-emerald-300">{r.satisfied}</td>
                            <td className="py-2 px-3 text-right font-mono text-red-300">{r.not_satisfied}</td>
                            <td className="py-2 px-3 text-right font-mono text-amber-300">{pct(r.satisfied_pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">No resolver activity yet.</p>
                )}
              </div>

              {/* Top Creators */}
              <div>
                <h4 className="text-sky-300 text-sm font-semibold mb-2">Top Creators (On-time Feedback)</h4>
                {leaderboardData?.top_creators?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left" data-testid="adv-top-creators-table">
                      <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
                        <tr>
                          <th className="py-2 px-3">Name</th>
                          <th className="py-2 px-3">Email</th>
                          <th className="py-2 px-3 text-right">Tickets</th>
                          <th className="py-2 px-3 text-right">Submitted</th>
                          <th className="py-2 px-3 text-right">Missed</th>
                          <th className="py-2 px-3 text-right">On-Time %</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-200">
                        {leaderboardData.top_creators.map((c, i) => (
                          <tr key={c.email || i} className="border-b border-slate-800 hover:bg-slate-800/50">
                            <td className="py-2 px-3">{c.name}</td>
                            <td className="py-2 px-3 text-slate-400 text-xs">{c.email}</td>
                            <td className="py-2 px-3 text-right font-mono">{c.tickets}</td>
                            <td className="py-2 px-3 text-right font-mono text-emerald-300">{c.feedback_submitted}</td>
                            <td className="py-2 px-3 text-right font-mono text-red-300">{c.feedback_missed}</td>
                            <td className="py-2 px-3 text-right font-mono text-sky-300">{pct(c.on_time_pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">No creator activity yet.</p>
                )}
              </div>

              {/* Flagged users */}
              <div>
                <h4 className="text-red-300 text-sm font-semibold mb-2 flex items-center gap-1">
                  <Flag className="w-4 h-4" /> Flagged ({leaderboardData?.flagged?.length ?? 0})
                </h4>
                {leaderboardData?.flagged?.length ? (
                  <div className="flex flex-wrap gap-2" data-testid="adv-flagged-list">
                    {leaderboardData.flagged.map((f, i) => (
                      <Badge
                        key={f.email || i}
                        variant="outline"
                        className="bg-red-950/50 text-red-200 border-red-700 px-2.5 py-1"
                      >
                        {f.name} · {f.role} · {f.missed_count} missed
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-xs">No users with ≥ 3 missed feedbacks. ✓</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedAnalyticsSection;
